import { db } from "../data/base";
import {
    collection,
    getDocs,
    getDoc,
    doc,
    query,
    where
} from "firebase/firestore";
import { secureGetItem } from "../data/utils";

// Retrieve user profile by email
export const getUserProfile = async (email) => {
    try {
        const q = query(collection(db, "Profiles"), where("_email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        // Take the first result (should be the only one)
        return {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
        };
    } catch (error) {
        console.error("Error retrieving profile:", error);
        throw error;
    }
};

// Retrieve catalogue details by ID
export const getCatalogueById = async (catalogueId) => {
    try {
        const docRef = doc(db, "Catalogues", catalogueId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error retrieving catalogue:", error);
        throw error;
    }
};

// Retrieve draft catalogue details by ID
export const getDraftedCatalogueById = async (catalogueId) => {
    try {
        const docRef = doc(db, "DraftedCatalogues", catalogueId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data(),
                isDraft: true
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error retrieving drafted catalogue:", error);
        throw error;
    }
};

// Retrieve all catalogues belonging to the user (published + drafts)
export const getUserCatalogues = async (userEmail = null) => {
    try {
        // Get email from secureStorage if not provided
        const email = userEmail || secureGetItem("email");

        if (!email) {
            console.error("No email available");
            return [];
        }

        // First we retrieve the user profile
        const userProfile = await getUserProfile(email);

        if (!userProfile) {
            return [];
        }

        // Catalogues pubblicati
        let publishedCatalogues = [];
        if (userProfile.list_catalogues && userProfile.list_catalogues.length) {
            const cataloguePromises = userProfile.list_catalogues.map(catalogueId =>
                getCatalogueById(catalogueId)
            );
            publishedCatalogues = (await Promise.all(cataloguePromises))
                .filter(catalogue => catalogue !== null)
                .map(catalogue => ({ ...catalogue, isDraft: false }));
        }

        // Cataloghi draft
        let draftedCatalogues = [];
        if (userProfile.list_drafted_catalogues && userProfile.list_drafted_catalogues.length) {
            const draftPromises = userProfile.list_drafted_catalogues.map(draftId =>
                getDraftedCatalogueById(draftId)
            );
            draftedCatalogues = (await Promise.all(draftPromises))
                .filter(draft => draft !== null);
        }

        // Unisci e restituisci: prima i draft, poi i pubblicati
        return [...draftedCatalogues, ...publishedCatalogues];
    } catch (error) {
        console.error("Error retrieving user catalogues:", error);
        throw error;
    }
};

// Fetch statistics data for a catalogue (products count, unique brands, unique SKUs)
export const getCatalogueStats = async (catalogueId) => {
    try {
        if (!catalogueId) {
            console.error("No catalogue ID provided");
            return { mainAssets: 0, brands: 0, sku: 0 };
        }

        // Query products for the specific catalogue
        const productsRef = collection(db, "CataloguesProducts");
        const productsQuery = query(productsRef, where("catalogRef", "==", catalogueId));
        const productsSnapshot = await getDocs(productsQuery);

        if (productsSnapshot.empty) {
            return { mainAssets: 0, brands: 0, sku: 0 };
        }

        // Count main assets (total products)
        const mainAssets = productsSnapshot.size;

        // Get unique brands
        const uniqueBrands = new Set();

        // Get unique SKUs from list_variants
        const uniqueSKUs = new Set();

        // For debugging
        let skuCount = 0;

        productsSnapshot.forEach(doc => {
            const data = doc.data();

            // Add brand to set if it exists
            if (data.mainBrandRef) {
                uniqueBrands.add(data.mainBrandRef);
            }

            // Check if the product has an SKU field directly
            if (data.sku) {
                uniqueSKUs.add(data.sku);
            }

            // Also check list_variants for SKUs
            if (data.list_variants && Array.isArray(data.list_variants)) {
                data.list_variants.forEach(variant => {
                    if (variant) {
                        // Try different possible SKU fields
                        if (variant.id) {
                            uniqueSKUs.add(variant.id);
                            skuCount++;
                        }
                        if (variant.sku) {
                            uniqueSKUs.add(variant.sku);
                            skuCount++;
                        }
                        if (variant.SKU) {
                            uniqueSKUs.add(variant.SKU);
                            skuCount++;
                        }
                        // If variant is just a string, add it directly
                        if (typeof variant === 'string') {
                            uniqueSKUs.add(variant);
                            skuCount++;
                        }
                    }
                });
            }
        });

        return {
            mainAssets,
            brands: uniqueBrands.size,
            sku: uniqueSKUs.size
        };
    } catch (error) {
        console.error("Error retrieving catalogue stats:", error);
        return { mainAssets: 0, brands: 0, sku: 0 };
    }
}; 